<template>
  <DbCarousel
    v-if="photos && photos.length > 0"
    :length="photos.length"
    :showFullscreenButton="showFullscreenButton"
    :showVideoButton="videos && videos.length > 0"
    v-on:click-fullscreen="
      initLightbox({ isOpen: true, title, content: photos, startIndex: $event })
    "
    v-on:click-video="initLightbox({ isOpen: true, title, content: media, startIndex: $event })"
  >
    <template v-slot:default="{ index }">
      <DbPicture
        :src="photos[index].url_standard"
        sizes="lg:500w md:500h sm:500h xs:500w"
      />
    </template>
  </DbCarousel>
  <!-- Lightbox for hotel card -->
  <Teleport to=".db-app">
    <DbModal
      v-if="lightbox.isOpen"
      class="db-modal-fullscreen db-modal-lightbox"
      v-on:close-modal="resetLightbox"
    >
      <template v-slot:header>
        <h1 class="db-title-1">
          {{ lightbox.title }}
        </h1>
      </template>

      <DbCarousel
        :length="lightbox.content.length"
        :defaultSlideIndex="lightbox.startIndex"
      >
        <template v-slot:default="{ index }">
          <div
            v-if="lightbox.content?.[index].type === 'video'"
            class="video"
            v-html="lightbox.content?.[index].player"
          />
          <DbImage
            v-else
            :src="lightbox.content?.[index].url_standard"
            :alt="lightbox.content?.[index].alt"
            :width="980"
          />
        </template>
        <template v-slot:thumbnail="{ index }">
          <span class="db-visually-hidden">
            {{ $t('carousel.Go to slide {x}', { x: index }) }}
          </span>
          <div
            v-if="lightbox.content?.[index].type === 'video'"
            class="db-video-thumbnail"
          >
            <DbImage
              v-if="lightbox.content?.[index].url_thumbnail"
              :src="lightbox.content?.[index].url_thumbnail"
              :width="100"
            />
            <svg
              class="db-thumbnail-play"
              viewBox="0 0 60 60"
            >
              <g>
                <path
                  d="M45.563,29.174l-22-15c-0.307-0.208-0.703-0.231-1.031-0.058C22.205,14.289,22,14.629,22,15v30
                  c0,0.371,0.205,0.711,0.533,0.884C22.679,45.962,22.84,46,23,46c0.197,0,0.394-0.059,0.563-0.174l22-15
                  C45.836,30.64,46,30.331,46,30S45.836,29.36,45.563,29.174z M24,43.107V16.893L43.225,30L24,43.107z"
                />
                <path
                  d="M30,0C13.458,0,0,13.458,0,30s13.458,30,30,30s30-13.458,30-30S46.542,0,30,0z M30,58C14.561,58,2,45.439,2,30
                  S14.561,2,30,2s28,12.561,28,28S45.439,58,30,58z"
                />
              </g>
            </svg>
          </div>

          <DbImage
            v-else
            :src="lightbox.content?.[index].url_standard"
            :alt="lightbox.content?.[index].alt"
            :width="100"
          />
        </template>
      </DbCarousel>
    </DbModal>
  </Teleport>
</template>
<script setup lang="ts">
import { reactive, computed } from 'vue'
import type { GalaxyUnitPhoto, GalaxyUnitVideo } from '@/types/galaxy-data/unit'

// TODO: typescript typing needs to be updated
// this works but it triggers errors

const props = withDefaults(
  defineProps<{
    title: string
    photos: GalaxyUnitPhoto[]
    videos?: GalaxyUnitVideo[]
    showFullscreenButton?: boolean
  }>(),
  {
    showFullscreenButton: true,
  },
)

interface ExtendContent extends GalaxyUnitVideo, GalaxyUnitPhoto {
  type: string
}

/** --- Lightbox implementation --- */
type LightboxDatas = {
  /** Status of the modal */
  isOpen: boolean
  /** Modal title */
  title: string
  /** Content to loop on for our case */
  content: ExtendContent[] | never[]
  /** Slider start index */
  startIndex?: number
}

/** Lightbox modal status */
const lightbox: LightboxDatas = reactive({
  isOpen: false,
  title: '',
  content: [],
  startIndex: 0,
})

const photoData = computed(() => {
  return [
    ...props.photos.map((item) => {
      return {
        type: 'photo',
        ...item,
      }
    }),
  ]
})

const videoData = computed(() => {
  return props.videos
    ? [
        ...props.videos.map((item) => {
          return {
            type: 'video',
            ...item,
          }
        }),
      ]
    : []
})

const media = computed(() => {
  if (props.videos) {
    return [...videoData.value, ...photoData.value]
  }
  return props.photos
})

/** Init the lightbox state */
function initLightbox({ isOpen, title, content, startIndex }: LightboxDatas) {
  lightbox.isOpen = isOpen
  lightbox.title = title
  lightbox.content = content
  lightbox.startIndex = startIndex !== undefined ? startIndex : 0
}

/** Reset the lightbox state */
function resetLightbox() {
  lightbox.isOpen = false
  lightbox.title = ''
  lightbox.content = []
  lightbox.startIndex = 0
}
</script>

<style scoped>
.db-video-thumbnail {
  position: relative;
  width: 54px;
  background-color: #e5e5e5;
}

.db-thumbnail-play {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  color: #fff;
  transform: translateX(-50%) translateY(-50%);
}

.db-thumbnail-play {
  fill: currentColor;
}

@media screen and (min-width: 992px) {
  .db-video-thumbnail {
    width: 90px;
  }

  .db-thumbnail-play {
    width: 34px;
    height: 34px;
  }
}
</style>
